import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, AlertCircle, CheckCircle2 } from "lucide-react";
import SiteNav from "../components/SiteNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONSULTATION = "Discovery Call";

export default function Booking() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    service: params.get("service") || "",
    date: "",
    time: "",
    notes: "",
  });
  const [eligible, setEligible] = useState(null); // null=unknown, true/false
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/services`).then(({ data }) => setServices(data)).catch(() => {});
  }, []);

  const needsConsultation = form.service && form.service !== CONSULTATION;

  const checkEligibility = useCallback(async (email) => {
    if (!email || !needsConsultation) {
      setEligible(null);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/bookings/eligibility`, { params: { email } });
      setEligible(data.has_consultation);
    } catch {
      setEligible(null);
    }
  }, [needsConsultation]);

  useEffect(() => {
    if (form.email && needsConsultation) checkEligibility(form.email);
    else setEligible(null);
  }, [form.service, form.email, needsConsultation, checkEligibility]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service || !form.date || !form.time) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setDone(true);
      toast.success("Booking requested. We'll confirm by email.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Booking failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-none border-0 border-b border-white/20 bg-transparent py-4 text-base font-light tracking-wide text-white placeholder-white/30 outline-none transition-colors focus:border-white";

  return (
    <div className="grain min-h-screen bg-[#0a0a0a] text-white">
      <SiteNav />

      <div className="mx-auto grid max-w-7xl grid-cols-1 pt-32 md:grid-cols-[1fr_1.2fr] md:pt-40">
        <div className="px-6 py-10 md:border-r md:border-white/10 md:px-12 md:py-20">
          <p className="mb-6 text-[10px] uppercase tracking-[0.4em] text-white/40">Reserve Your Session</p>
          <h1 className="font-serif-lux text-5xl font-light leading-none tracking-tight md:text-6xl">
            Book with<br />Amani.
          </h1>
          <p className="mt-8 max-w-sm text-sm font-light leading-relaxed tracking-wide text-white/50">
            A Discovery Call is the first step for every creator. Once your call is booked, all other services unlock.
          </p>
          <div className="mt-10 space-y-4 border-t border-white/10 pt-8">
            {services.map((s, i) => (
              <div key={s.key} className="flex items-baseline justify-between text-sm">
                <span className="font-light tracking-wide text-white/70">{s.name}</span>
                <span className="font-serif-lux text-xs italic text-white/30">
                  {s.requires_consultation ? "requires discovery call" : "start here"}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 py-10 md:px-14 md:py-20">
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex h-full flex-col items-start justify-center"
              data-testid="booking-success"
            >
              <CheckCircle2 size={40} strokeWidth={1} className="text-white" />
              <h2 className="font-serif-lux mt-6 text-4xl font-light tracking-tight">Request received.</h2>
              <p className="mt-4 max-w-sm text-sm font-light leading-relaxed text-white/60">
                Thank you, {form.name.split(" ")[0]}. We've received your request for {form.service}. Our team will confirm the details by email shortly.
              </p>
              <div className="mt-10 flex gap-6">
                <button
                  data-testid="booking-view-services"
                  onClick={() => navigate("/services")}
                  className="text-[11px] uppercase tracking-[0.3em] text-white/60 hover:text-white"
                >
                  View Services
                </button>
                <button
                  data-testid="booking-another"
                  onClick={() => {
                    setDone(false);
                    setForm({ name: form.name, email: form.email, service: "", date: "", time: "", notes: "" });
                  }}
                  className="text-[11px] uppercase tracking-[0.3em] text-white/60 hover:text-white"
                >
                  Book Another
                </button>
              </div>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8" data-testid="booking-form">
              <select
                name="service"
                value={form.service}
                onChange={handleChange}
                data-testid="booking-service"
                className={`${inputCls} cursor-pointer [&>option]:bg-[#0a0a0a]`}
              >
                <option value="" disabled>
                  Select a service
                </option>
                {services.map((s) => (
                  <option key={s.key} value={s.name}>
                    {s.name}
                    {s.requires_consultation ? " — discovery call required" : ""}
                  </option>
                ))}
              </select>

              <input type="text" name="name" value={form.name} onChange={handleChange} placeholder="Full Name" data-testid="booking-name" className={inputCls} />
              <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="Email Address" data-testid="booking-email" className={inputCls} />

              {needsConsultation && eligible === false && (
                <div data-testid="booking-consultation-warning" className="flex items-start gap-3 border border-white/15 bg-white/[0.03] p-4 text-xs font-light leading-relaxed text-white/70">
                  <AlertCircle size={16} strokeWidth={1.4} className="mt-0.5 shrink-0" />
                  <span>
                    A Discovery Call is required before booking <strong className="font-medium text-white">{form.service}</strong>. Please book your Discovery Call first.{" "}
                    <button
                      type="button"
                      data-testid="booking-switch-consultation"
                      onClick={() => setForm({ ...form, service: CONSULTATION })}
                      className="underline underline-offset-2 hover:text-white"
                    >
                      Book Discovery Call
                    </button>
                  </span>
                </div>
              )}
              {needsConsultation && eligible === true && (
                <div className="flex items-center gap-2 text-xs font-light text-white/50">
                  <CheckCircle2 size={14} strokeWidth={1.4} /> Discovery Call on file — you're all set.
                </div>
              )}

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="mb-2 block text-[10px] uppercase tracking-[0.2em] text-white/40">Preferred Date</label>
                  <input type="date" name="date" value={form.date} onChange={handleChange} data-testid="booking-date" className={`${inputCls} [color-scheme:dark]`} />
                </div>
                <div>
                  <label className="mb-2 block text-[10px] uppercase tracking-[0.2em] text-white/40">Preferred Time</label>
                  <input type="time" name="time" value={form.time} onChange={handleChange} data-testid="booking-time" className={`${inputCls} [color-scheme:dark]`} />
                </div>
              </div>

              <textarea name="notes" value={form.notes} onChange={handleChange} placeholder="Anything we should know? (optional)" rows={2} data-testid="booking-notes" className={`${inputCls} resize-none`} />

              <button
                type="submit"
                disabled={submitting || (needsConsultation && eligible === false)}
                data-testid="booking-submit-button"
                className="group inline-flex items-center gap-3 rounded-none bg-white px-10 py-4 text-[11px] uppercase tracking-[0.3em] text-black transition-colors hover:bg-white/80 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {submitting ? "Booking" : "Confirm Booking"}
                <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
